/**
 * Logique pure du Lot B (Kanban + « À faire ») : tri des colonnes, priorité, position du menu
 * contextuel, libellés « il y a 12 min », prénoms, regroupement des tâches.
 */
import type { Case } from "./useRealtimeCases";
import { caseTaskLabel, type CaseTask } from "./caseTasks";

type PriorityFields = Pick<Case, "is_priority">;

/** Pastille rouge « Prioritaire » : uniquement la priorité posée par le manager (plus de priorité automatique au montant, Paul 04/11). */
export function isCasePriority(c: PriorityFields): boolean {
  return !!c.is_priority;
}

type SortFields = PriorityFields & Pick<Case, "created_at">;

/**
 * Ordre des cartes d'une colonne (tri stable, ne modifie pas l'entrée) :
 * - toutes les colonnes : dossiers marqués prioritaires par le manager d'abord ;
 * - « Non traité » : ensuite les plus anciens d'abord (plus de tri automatique au montant, Paul 04/11).
 */
export function sortColumnCases<T extends SortFields>(cases: readonly T[], status: string): T[] {
  const manual = (c: T) => Number(!!c.is_priority);
  return cases
    .map((c, index) => ({ c, index }))
    .sort((a, b) => {
      const byManual = manual(b.c) - manual(a.c);
      if (byManual) return byManual;
      if (status === "Non traité") {
        const byAge = new Date(a.c.created_at).getTime() - new Date(b.c.created_at).getTime();
        if (byAge) return byAge;
      }
      return a.index - b.index;
    })
    .map(({ c }) => c);
}

/** Place un menu de taille (width × height) au point (x, y) sans qu'il sorte de la fenêtre. */
export function clampMenuPosition(
  x: number,
  y: number,
  width: number,
  height: number,
  viewportWidth: number,
  viewportHeight: number,
  margin = 8,
): { left: number; top: number } {
  const maxLeft = Math.max(margin, viewportWidth - width - margin);
  const maxTop = Math.max(margin, viewportHeight - height - margin);
  return {
    left: Math.min(Math.max(margin, x), maxLeft),
    top: Math.min(Math.max(margin, y), maxTop),
  };
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** « à l'instant », « il y a 12 min », « il y a 3 h », « il y a 2 j ». */
export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  const at = iso ? Date.parse(iso) : NaN;
  if (Number.isNaN(at)) return "";
  const diff = Math.max(0, now - at);
  if (diff < MINUTE) return "à l'instant";
  if (diff < HOUR) return `il y a ${Math.floor(diff / MINUTE)} min`;
  if (diff < DAY) return `il y a ${Math.floor(diff / HOUR)} h`;
  return `il y a ${Math.floor(diff / DAY)} j`;
}

interface NamedProfile {
  full_name?: string | null;
  email?: string | null;
}

/** Prénom affichable : premier mot du nom complet, sinon début de l'e-mail, sinon « quelqu'un ». */
export function firstNameOf(profile: NamedProfile | null | undefined, fallback = "un collègue"): string {
  const fullName = profile?.full_name?.trim();
  if (fullName) return fullName.split(/\s+/)[0];
  const local = profile?.email?.split("@")[0]?.trim();
  if (local) {
    const word = local.split(/[._-]+/)[0];
    return word.charAt(0).toUpperCase() + word.slice(1);
  }
  return fallback;
}

/** Plus anciennes d'abord (liste « À faire »), tri stable par id. */
export function oldestTasksFirst(tasks: readonly CaseTask[]): CaseTask[] {
  return [...tasks].sort((a, b) =>
    a.created_at === b.created_at ? a.id.localeCompare(b.id) : a.created_at < b.created_at ? -1 : 1,
  );
}

/** Tâches regroupées par dossier (plus anciennes d'abord dans chaque groupe). */
export function tasksByCase(tasks: readonly CaseTask[]): Map<string, CaseTask[]> {
  const map = new Map<string, CaseTask[]>();
  for (const task of oldestTasksFirst(tasks)) {
    const list = map.get(task.case_id);
    if (list) list.push(task);
    else map.set(task.case_id, [task]);
  }
  return map;
}

/** Texte de la flamme : « Tâche à faire : Envoyer le devis » / « 2 tâches à faire : … ». */
export function flameLabel(tasks: readonly Pick<CaseTask, "task_type">[]): string {
  const labels = [...new Set(tasks.map((task) => caseTaskLabel(task.task_type)))];
  if (tasks.length <= 1) return `Tâche à faire : ${labels[0] ?? ""}`.trim();
  return `${tasks.length} tâches à faire : ${labels.join(", ")}`;
}

interface MemberLike {
  profile_id: string;
  profile: (NamedProfile & { role?: string | null }) | null;
}

/** Membres d'une équipe qui recevront la tâche : hors manager(s) et hors auteur. */
export function teamRecipients<T extends MemberLike>(members: readonly T[], authorId: string | null | undefined): T[] {
  return members.filter((member) => member.profile_id !== authorId && member.profile?.role !== "sales_manager");
}
